import {
  Autocomplete,
  CircularProgress,
  MenuItem,
  Select,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CommonTextField from "../../../../common/components/functional/commonTextField/CommonTextField";
import { NameIdOption } from "../../../../common/types/commonTypes";
import {
  fetchTesSalaryTablesByTes,
  TesSalaryTableDocument,
} from "../../../tesSalaryTables/tesSalaryTablesApi";
import {
  BULK_SALARY_INCREASE_FILTER_NAMES,
} from "../constants";
import { BulkSalaryIncreaseFilterValues } from "../types";
import useEligibleTesForBulkSalaryTableIncrease from "../useEligibleTesForBulkSalaryTableIncrease";
import BulkSalaryIncreaseFilterCell, {
  bulkSalaryIncreaseFilterGridWidths,
} from "./BulkSalaryIncreaseFilterCell";

const publishedTableLabel = (t: TesSalaryTableDocument) =>
  `${t.name} (${t.effectiveDate})`;

type Props = {
  getFilter: (
    name: BULK_SALARY_INCREASE_FILTER_NAMES,
  ) => { displayName?: string } | undefined;
};

const BulkSalaryIncreaseTesTablesTargetFields = ({ getFilter }: Props) => {
  const { t } = useTranslation();
  const { options: eligibleTesOptions, loading: loadingTes } =
    useEligibleTesForBulkSalaryTableIncrease(true);
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<BulkSalaryIncreaseFilterValues>();

  const tes = watch(BULK_SALARY_INCREASE_FILTER_NAMES.TES);
  const tesId = tes?.id ?? null;
  const [publishedTables, setPublishedTables] = useState<
    TesSalaryTableDocument[]
  >([]);
  const [loadingTables, setLoadingTables] = useState(false);

  useEffect(() => {
    if (!tesId) {
      setPublishedTables([]);
      return;
    }
    let cancelled = false;
    setLoadingTables(true);
    fetchTesSalaryTablesByTes(tesId, { status: "PUBLISHED" })
      .then((rows) => {
        if (!cancelled) {
          setPublishedTables(rows);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPublishedTables([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingTables(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tesId]);

  const eligibleAsNameId: NameIdOption[] = eligibleTesOptions.map((o) => ({
    id: o.id,
    name: o.name,
  }));

  return (
    <>
      <BulkSalaryIncreaseFilterCell
        label={getFilter(BULK_SALARY_INCREASE_FILTER_NAMES.TES)?.displayName}
        itemWidths={bulkSalaryIncreaseFilterGridWidths.threePerRowFromMd}
      >
        <Controller
          name={BULK_SALARY_INCREASE_FILTER_NAMES.TES}
          control={control}
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <Autocomplete
              value={value}
              options={eligibleAsNameId}
              loading={loadingTes}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, v) => option.id === v.id}
              onChange={(_, newValue) => {
                setValue("tesSalaryTableSourceId", "");
                setValue("tesSalaryTableTargetId", "");
                onChange(newValue);
              }}
              renderInput={(params) => (
                <CommonTextField
                  {...params}
                  sx={{ maxWidth: { xs: "100%", md: 360 } }}
                  error={!!errors[BULK_SALARY_INCREASE_FILTER_NAMES.TES]}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingTes ? (
                          <CircularProgress color="inherit" size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
        />
      </BulkSalaryIncreaseFilterCell>

      <BulkSalaryIncreaseFilterCell
        label={t("settings:bulkSalaryIncrease.tesTablesSourceTable")}
        itemWidths={bulkSalaryIncreaseFilterGridWidths.threePerRowFromMd}
      >
        <Controller
          name="tesSalaryTableSourceId"
          control={control}
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <Select
              value={value || ""}
              onChange={(e) => onChange(e.target.value || "")}
              disabled={!tesId || loadingTables}
              displayEmpty
              fullWidth
              size="small"
              sx={{ maxWidth: { xs: "100%", md: 360 } }}
              error={!!errors.tesSalaryTableSourceId}
            >
              <MenuItem value="">
                <em>{t("settings:bulkSalaryIncrease.tesTablesPickTable")}</em>
              </MenuItem>
              {publishedTables.map((row) => (
                <MenuItem
                  key={row.tesSalaryTableId}
                  value={row.tesSalaryTableId}
                >
                  {publishedTableLabel(row)}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </BulkSalaryIncreaseFilterCell>

      <BulkSalaryIncreaseFilterCell
        label={t("settings:bulkSalaryIncrease.tesTablesTargetTable")}
        itemWidths={bulkSalaryIncreaseFilterGridWidths.threePerRowFromMd}
      >
        <Controller
          name="tesSalaryTableTargetId"
          control={control}
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <Select
              value={value || ""}
              onChange={(e) => onChange(e.target.value || "")}
              disabled={!tesId || loadingTables}
              displayEmpty
              fullWidth
              size="small"
              sx={{ maxWidth: { xs: "100%", md: 360 } }}
              error={!!errors.tesSalaryTableTargetId}
            >
              <MenuItem value="">
                <em>{t("settings:bulkSalaryIncrease.tesTablesPickTable")}</em>
              </MenuItem>
              {publishedTables.map((row) => (
                <MenuItem
                  key={`tgt-${row.tesSalaryTableId}`}
                  value={row.tesSalaryTableId}
                >
                  {publishedTableLabel(row)}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </BulkSalaryIncreaseFilterCell>
    </>
  );
};

export default BulkSalaryIncreaseTesTablesTargetFields;
